import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link as RouterLink } from 'react-router'
import { Container } from '@carouselgg/nexus/container'
import { Text } from '@carouselgg/nexus/text'
import { Link } from '@carouselgg/nexus/link'
import { Spinner } from '@carouselgg/nexus/spinner'
import { RichTextContent } from '@carouselgg/nexus/rich-text-content'
import { ArticleHeader } from '@carouselgg/nexus/article-header'
import { RelatedStories } from '@carouselgg/nexus/related-stories'
import { ArticleEmbed } from '@carouselgg/nexus/article-embed'
import { useAuthStore } from '@carouselgg/auth/store'
import type { NewsStory } from '@carouselgg/nexus/news-front'
import { Link2, Share2 } from '@carouselgg/nexus/icon'
import { toast } from '@carouselgg/nexus/toast'
import { getPlatformArticleBySlug, listPlatformArticles, recordArticleView, toStory } from '../api/content'
import type { PlatformArticle } from '../api/content'
import styles from './ArticlePage.module.css'

export function ArticlePage() {
  const { slug } = useParams()
  const [article, setArticle] = useState<PlatformArticle | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [related, setRelated] = useState<NewsStory[]>([])
  const [next, setNext] = useState<NewsStory | undefined>()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setLoading(true)
    setNotFound(false)
    getPlatformArticleBySlug(slug)
      .then((a) => { if (!cancelled) setArticle(a) })
      .catch(() => { if (!cancelled) setNotFound(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [slug])

  const articleId = article?.id
  const topic = article?.tags[0]

  useEffect(() => {
    if (articleId) recordArticleView(articleId)
  }, [articleId])

  const embeds = useMemo(() => new Map((article?.embeds ?? []).map((e) => [e.ref, e])), [article?.embeds])
  const renderEmbed = useCallback((line: string) => {
    const e = embeds.get(line)
    return e ? <ArticleEmbed embed={e} /> : null
  }, [embeds])

  useEffect(() => {
    if (!articleId) return
    let cancelled = false
    Promise.all([
      topic ? listPlatformArticles(4, 0, topic) : Promise.resolve({ articles: [] as PlatformArticle[] }),
      listPlatformArticles(8, 0),
    ])
      .then(([byTopic, latest]) => {
        if (cancelled) return
        const relatedArticles = byTopic.articles.filter((a) => a.id !== articleId).slice(0, 3)
        const shown = new Set([articleId, ...relatedArticles.map((a) => a.id)])
        const nextArticle = latest.articles.find((a) => !shown.has(a.id))
        setRelated(relatedArticles.map(toStory))
        setNext(nextArticle ? toStory(nextArticle) : undefined)
      })
      // More-from is a nicety; the article itself already loaded.
      .catch(() => { if (!cancelled) { setRelated([]); setNext(undefined) } })
    return () => { cancelled = true }
  }, [articleId, topic])

  async function copyLink() {
    if (!article) return
    await navigator.clipboard.writeText(`${window.location.origin}/news/${article.slug}`)
    toast.success('Link copied to clipboard')
  }

  async function share() {
    if (!article) return
    const url = `${window.location.origin}/news/${article.slug}`
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, text: article.excerpt, url })
      } catch {
        // Cancelled by the reader
      }
    } else {
      await copyLink()
    }
  }

  const back = (
    <Link as={RouterLink} to="/news" variant="muted" className={styles.backLink}>
      <Text as="span" size="sm" muted>&larr; Carousel News</Text>
    </Link>
  )

  if (loading) {
    return (
      <Container size="xl">
        <div className={styles.loading}><Spinner size="md" /></div>
      </Container>
    )
  }

  if (notFound || !article) {
    return (
      <Container size="xl">
        <div className={styles.notFound}>
          <Text as="h1" size="lg" weight="semibold">Article not found</Text>
          {back}
        </div>
      </Container>
    )
  }

  return (
    <div className={styles.page}>
      <Container size="xl">
        {back}
        <ArticleHeader
          title={article.title}
          dek={article.excerpt}
          coverUrl={article.cover_image_url}
          kicker={article.tags[0]}
          author={article.author_name}
          date={article.published_at}
          readTimeMinutes={article.read_time_minutes}
          tags={article.tags}
          tagHref={(tag) => `/news?topic=${encodeURIComponent(tag)}`}
        />

        <div className={styles.layout}>
          <aside className={styles.share} aria-label="Share">
            <button type="button" className={styles.shareButton} onClick={share} aria-label="Share article">
              <Share2 size={16} aria-hidden="true" />
            </button>
            <button type="button" className={styles.shareButton} onClick={copyLink} aria-label="Copy link">
              <Link2 size={16} aria-hidden="true" />
            </button>
          </aside>
          <article className={styles.body}>
            <RichTextContent markdown={article.body} embed={renderEmbed} />
            {!isAuthenticated && (
              <aside className={styles.signup} aria-labelledby="article-signup">
                <div>
                  <h2 id="article-signup" className={styles.signupTitle}>See your Trust Score</h2>
                  <Text size="sm" muted>Free. Link a game account and find a league where every roster is verified.</Text>
                </div>
                <RouterLink to="/signup" className={styles.signupButton}>Create your account</RouterLink>
              </aside>
            )}
          </article>
        </div>

        <RelatedStories title="More Carousel news" stories={related} next={next} className={styles.related} />
      </Container>
    </div>
  )
}
